// The Rückfragen of one voice session (VoiceCardController), asked one at a time.
//
// Two sources: a voice pass returns what stayed unclear (`questions` of
// voice_extract: an unclear name, an answer that fits no option), and the
// rule-based check (voice_questions) returns the required fields the form still
// leaves empty. A field is asked once: a question about what was heard wins over
// "required" for the same field. Skipped and answered questions never come back
// in the same session; their ids are stable across passes.
export const targetKey = (target = {}) =>
  [target.bucket, target.row ?? "", target.scope, target.key].join("/")

export class VoiceQuestionQueue {
  constructor() {
    this.reset()
  }

  reset() {
    this.fromPass = []
    this.required = []
    this.skipped = new Set()
    // This round's answers (shown as "Alles erfasst"); earlier rounds' ids only stay closed.
    this.answered = []
    this.answeredBefore = new Set()
    this.shown = 0
  }

  // A new pass replaces the open questions of the passes before it and starts a
  // new round; an answer pass only adds what it still asks about its own field.
  // +answered+: whether the answer pass filled its field — if not, the question
  // stays open unless the pass asks about that field anew.
  addFromPass(questions, { answering = null, answered = true } = {}) {
    const asked = (questions || []).filter((question) => !this.closed(question.id))
    if (answering) {
      const key = targetKey(answering.target)
      const replaced = answered || asked.some((question) => targetKey(question.target) === key)
      if (replaced) this.fromPass = this.fromPass.filter((question) => question.id !== answering.id)
      this.fromPass.push(...asked.filter((question) => !this.has(question.id)))
    } else {
      this.answered.forEach(({ question }) => this.answeredBefore.add(question.id))
      this.answered = []
      this.fromPass = asked
      this.shown = 0
    }
  }

  setRequired(questions) {
    this.required = (questions || []).filter((question) => !this.closed(question.id))
  }

  // Open questions in asking order.
  pending() {
    const heard = new Set(this.fromPass.map((question) => targetKey(question.target)))
    const required = this.required.filter(
      (question) => !heard.has(targetKey(question.target)) && !this.has(question.id),
    )
    return [...this.fromPass, ...required].filter((question) => !this.closed(question.id))
  }

  current() {
    return this.pending()[0] || null
  }

  // "Rückfrage 2 von 3": how many were shown this round plus what is still open.
  position() {
    const open = this.pending().length
    return { index: this.shown + 1, total: this.shown + open }
  }

  skip(question) {
    this.skipped.add(question.id)
    this.shown += 1
  }

  answer(question, value) {
    this.answered.push({ question, value })
    this.shown += 1
  }

  // Ids the server should not ask again (voice_questions `skipped`).
  closedIds() {
    return [...this.skipped, ...this.answeredIds()]
  }

  // Question ids still open or answered — their warnings are not shown as hints.
  coveredIds() {
    return new Set([...this.pending().map((question) => question.id), ...this.answeredIds()])
  }

  closed(id) {
    return this.skipped.has(id) || this.answeredIds().includes(id)
  }

  answeredIds() {
    return [...this.answeredBefore, ...this.answered.map(({ question }) => question.id)]
  }

  has(id) {
    return this.fromPass.some((question) => question.id === id)
  }
}
